/**
 * THE CANDIDATE READ for the relationship workspace (#2351).
 *
 * One hook, two governed branches, and the difference between them is stated
 * rather than smoothed over — because it decides what the surface may offer:
 *
 *   `door`  — the target entity registers a `search_*` RPC, discovered exactly
 *             as ⌘K discovers it. Ranked whole-table under the caller's own
 *             RLS, and it takes a query and a limit: no offset, no total, no
 *             order, no filter. So paging is a limit+1 PROBE and a widening
 *             window, the count reads "showing N", and the only sort is the
 *             door's own rank.
 *   `table` — no door, so the entity's own table is read server-side with an
 *             `ilike` over its declared text columns (`textSearchFilter`, which
 *             is sanitised rather than escaped). `selectPage` reports the
 *             Content-Range total, so this branch pages truthfully and can
 *             order and filter.
 *
 * Neither branch reads a pool and filters it in the browser. That defect —
 * "50 rows off the top of the table", #1075 F1 — is what made the old picker a
 * duplicate factory, and `useRelationSearch`'s own header records it.
 *
 * Tier 1 — console wiring only. No SQL, no policy, no grant: it composes the
 * governed reads that already exist.
 */
import { useCallback, useMemo, useState } from 'react'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import type { Row } from '@solum/sdk'
import { useDebouncedValue } from '@solum/ui'
import { buildRpcArgs, rowsToHits } from '../app/federatedSearch'
import { displayLabel, GOVERNED_SEARCH_ONLY, governedWordSearch, useEntityIdentities, useEntityTable, useReadRoute } from '../data/queries'
import { useSolum } from '../data/client'
import {
  candidateOrder, candidateSorts, activeFilterAvailable, linkedEndpointIds, toCandidateWindow,
  ACTIVE_COLUMN, type CandidateBranch, type CandidateSort, type CandidateWindow,
} from './relationCandidates'
import type { RelationSlot } from './relations'
import { textSearchFilter, useRelationRows, useRelationTargetFields, useSearchTools } from './useRelations'

/** How many candidates one page of the workspace shows. */
export const CANDIDATE_WINDOW = 20

export interface CandidateRequest {
  query: string
  sort: CandidateSort
  /** On by default: an existing edge is not a candidate. */
  hideLinked: boolean
  activeOnly: boolean
  /** Grows by CANDIDATE_WINDOW each time the person asks for more. */
  window: number
}

export interface CandidateState extends CandidateWindow {
  branch: CandidateBranch
  /** The sorts this branch can honour — empty means draw no control at all. */
  sorts: CandidateSort[]
  /** Whether an "Active only" filter exists for this target. */
  activeOffered: boolean
  /** The ids this record is already linked to through this slot. */
  linked: ReadonlySet<string>
  isLoading: boolean
  /** The candidate read itself failed or was refused — not "nothing matches". */
  isError: boolean
  error: unknown
  /** The existing-edge read failed, so "already linked" is UNKNOWN. */
  linkedUnknown: boolean
}

/** Today, as the database would compare it. Overridable so a test can pin it. */
const today = () => new Date().toISOString().slice(0, 10)

export function useRelationCandidates(
  slot: RelationSlot | null,
  recordId: string,
  request: CandidateRequest,
): CandidateState {
  const { client } = useSolum()
  const entity = slot?.otherEntity ?? ''
  const fields = useRelationTargetFields(slot)
  const tools = useSearchTools()
  const table = useEntityTable(entity)
  const route = useReadRoute(entity)
  const governed = route.governed
  /* A MODULE DOOR (#2759) answers only what its module DECLARED: a sort is
     offered only on a declared sort column, and "active only" only where
     `is_active = true` is a declared filter — refused at the menu, never
     dropped from the read (rule 4). */
  const identities = useEntityIdentities()
  const tool = useMemo(() => tools.find((t) => t.entity === entity) ?? null, [tools, entity])
  const typeLabel = useMemo(
    () => identities.data?.find((e) => e.entity_type === entity)?.singular_label ?? null,
    [identities.data, entity])

  /* The EXISTING edges of this slot, which is where "already linked" comes
     from. Its failure is carried separately: an unreadable edge list is not an
     empty one, and drawing every candidate as linkable over edges RLS is hiding
     invites a duplicate the database will then refuse (#389's shape, which
     `useRelationRows`' own header warns about one hook along). */
  const existing = useRelationRows(slot, recordId)
  const linked = useMemo(
    () => linkedEndpointIds(existing.rows, today()),
    [existing.rows])

  const branch: CandidateBranch = tool ? 'door' : table ? 'table' : 'none'
  const sorts = useMemo(() => candidateSorts(branch, fields).filter((s) => {
    const order = candidateOrder(s, fields, governed)
    return order === undefined || !route.capabilities || route.capabilities.sort.includes(order.split('.')[0]!)
  }), [branch, fields, governed, route.capabilities])
  const activeOffered = useMemo(() => activeFilterAvailable(branch, fields)
    && (!route.capabilities || (route.capabilities.filter[ACTIVE_COLUMN] ?? []).includes('eq')),
  [branch, fields, route.capabilities])
  const sort = sorts.includes(request.sort) ? request.sort : sorts[0] ?? 'best'
  const activeOnly = activeOffered && request.activeOnly

  // 200ms, the same as ⌘K and the context page: the keystroke never blocks, and
  // one shared helper rather than a third debounce implementation.
  const query = useDebouncedValue(request.query.trim(), 200)

  const read = useQuery({
    queryKey: ['relation-candidates', branch, entity, tool?.fn ?? null, query, sort, activeOnly, request.window],
    enabled: branch !== 'none',
    // The previous page stays on screen while the next one is in flight, so the
    // list does not blink to empty under the person's hand — and an empty list
    // is a claim ("nothing matches") that must not be made while asking.
    placeholderData: keepPreviousData,
    queryFn: async (): Promise<{ rows: Row[]; total: number | null }> => {
      // ONE ROW MORE than the window, deliberately: it is the only way the door
      // branch can tell a full page from a complete answer.
      const limit = request.window + 1
      if (tool) {
        const rows = await client.raw().rpcTable<Row>(
          tool.fn, buildRpcArgs(tool, query, limit), tool.schema)
        return { rows: Array.isArray(rows) ? rows : [], total: null }
      }
      if (!table) return { rows: [], total: null }
      // A governed target with no search door cannot be searched by words
      // without disclosing rows nobody asked for (#2444) — unless its module
      // DECLARES a search, which answers through its own lookup (#2759).
      // without disclosing rows nobody asked for (#2444): say so.
      if (governed && query) {
        const hits = await governedWordSearch(client.raw(), route, entity, query, limit)
        if (!hits) throw new Error(GOVERNED_SEARCH_ONLY)
        return { rows: hits.map((h) => ({ id: h.id, name: h.title })), total: null }
      }
      const filters = [
        ...(textSearchFilter(fields, query) ?? []),
        ...(activeOnly ? [{ key: ACTIVE_COLUMN, value: 'is.true' }] : []),
      ]
      const page = await client.raw().selectPage<Row>(table.schema_name, table.table_name, {
        limit,
        ...(filters.length > 0 ? { filters } : {}),
        ...(candidateOrder(sort, fields, governed) ? { order: candidateOrder(sort, fields, governed) } : {}),
      })
      return { rows: page.rows, total: page.total }
    },
  })

  const label = useMemo(() => (row: Row) => {
    if (tool) {
      // The door's own shaping — id, the first text column, the rest folded
      // into a detail line — which is what tells two people with one name
      // apart. It never labels a row with its uuid (#1705, fixed in #2351).
      const [hit] = rowsToHits([row], typeLabel)
      return { primary: hit?.label ?? displayLabel(row, typeLabel), secondary: hit?.detail ?? null }
    }
    return { primary: displayLabel(row, typeLabel), secondary: null }
  }, [tool, typeLabel])

  const shaped = useMemo(() => toCandidateWindow(read.data?.rows ?? [], {
    fields,
    linked,
    // A candidate cannot be hidden as "already linked" while that read is
    // broken: it would silently shorten the list on the strength of an answer
    // nobody got.
    hideLinked: request.hideLinked && !existing.isError,
    window: request.window,
    total: read.data?.total ?? null,
    label,
  }), [read.data, fields, linked, request.hideLinked, request.window, existing.isError, label])

  return {
    ...shaped,
    branch,
    sorts,
    activeOffered,
    linked,
    isLoading: read.isLoading || (branch !== 'none' && read.isPending),
    isError: read.isError,
    error: read.error,
    linkedUnknown: existing.isError,
  }
}

/**
 * The request state, with the defaults #2351 asks for.
 *
 * Every callback is STABLE (`useCallback`), which is load-bearing rather than
 * tidy: the workspace resets the request in an effect keyed on the relationship
 * type, and a `reset` rebuilt on each render would make that effect run on
 * every render — a reset loop that clears the query the person is typing.
 */
export function useCandidateRequest(sortDefault: CandidateSort = 'best') {
  const initial = useMemo<CandidateRequest>(() => ({
    query: '', sort: sortDefault, hideLinked: true, activeOnly: false, window: CANDIDATE_WINDOW,
  }), [sortDefault])
  const [request, setRequest] = useState<CandidateRequest>(initial)
  return {
    request,
    /* Any change to what is being ASKED resets the window: keeping a widened
       window across a new query would ask the server for 60 rows of something
       the person has not looked at yet. */
    set: useCallback((patch: Partial<CandidateRequest>) => setRequest((current) => ({
      ...current,
      ...patch,
      window: patch.window ?? (
        Object.keys(patch).some((k) => k !== 'window') ? CANDIDATE_WINDOW : current.window),
    })), []),
    more: useCallback(
      () => setRequest((current) => ({ ...current, window: current.window + CANDIDATE_WINDOW })),
      []),
    reset: useCallback(() => setRequest(initial), [initial]),
  }
}
